import Loading from 'core/modules/atoms/Loading';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import TutorAITabs from '../../components/atoms/TutorAITabs/TutorAITabs';
import AnalyticsFilterBar from '../../components/compositions/AnalyticsFilterBar/AnalyticsFilterBar';
import TeacherLayoutWrapper from '../../components/layouts/TeacherLayoutWrapper/TeacherLayoutWrapper';
import { useAnalyticsFilters } from '../../contexts/AnalyticsFiltersContext';
import './_teacher-ai-analytics-panel.scss';
import IntentDistributionSection from './sections/IntentDistributionSection/IntentDistributionSection';
import TopicRankingSection from './sections/TopicRankingSection/TopicRankingSection';
import TutorUsageSection from './sections/TutorUsageSection/TutorUsageSection';

/**
 * TeacherAIAnalyticsPanelView - Main view for the AI analytics panel.
 * Displays class and time period filters with an empty content area for future phases.
 */
function TeacherAIAnalyticsPanelView(): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const { allCourseIds, groupCourseMap, loading, selectedClass, selectedTimePeriod } = useAnalyticsFilters();
  const [activeTab, setActiveTab] = useState<string>('tutor-use');

  if (loading) {
    return <Loading />;
  }

  return (
    <TeacherLayoutWrapper activeNavigationId="analytics" sidebarMode="expanded" showBackArrowWhenCollapsed={true} contentPadding="default">
      <div className="teacher-ai-analytics-panel">
        <div className="teacher-ai-analytics-panel__header">
          <div className="teacher-ai-analytics-panel__header-titles">
            <h1 className="teacher-ai-analytics-panel__title">{t('chatbot:ai_analytics_panel_title')}</h1>
            <p className="teacher-ai-analytics-panel__subtitle">{t('chatbot:ai_analytics_panel_subtitle')}</p>
          </div>
          <div className="teacher-ai-analytics-panel__header-filters">
            <AnalyticsFilterBar />
          </div>
        </div>
        <div className="teacher-ai-analytics-panel__content">
          <div className="teacher-ai-analytics-panel__content-tabs">
            <TutorAITabs
              tabs={[
                { id: 'tutor-use', label: t('chatbot:ai_analytics_tab_tutor_use') },
                { id: 'topic-ranking', label: t('chatbot:ai_analytics_tab_queries') },
                { id: 'intent-distribution', label: t('chatbot:ai_analytics_tab_performance') },
              ]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
          {activeTab === 'tutor-use' && (
            <TutorUsageSection
              allCourseIds={allCourseIds}
              classValue={selectedClass.value}
              groupCourseMap={groupCourseMap}
              timePeriodValue={selectedTimePeriod.value}
            />
          )}
          {activeTab === 'topic-ranking' && (
            <TopicRankingSection
              allCourseIds={allCourseIds}
              classValue={selectedClass.value}
              groupCourseMap={groupCourseMap}
              timePeriodValue={selectedTimePeriod.value}
            />
          )}
          {activeTab === 'intent-distribution' && (
            <IntentDistributionSection
              allCourseIds={allCourseIds}
              classValue={selectedClass.value}
              groupCourseMap={groupCourseMap}
              timePeriodValue={selectedTimePeriod.value}
            />
          )}
        </div>
      </div>
    </TeacherLayoutWrapper>
  );
}

export default TeacherAIAnalyticsPanelView;
